import { devices } from '@src/styles/theme';
import { avatarInitial, avatarTint } from '@src/utils/chatAvatar';
import { formatRelativeTime } from '@src/utils/donationFormat';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';

/**
 * 커뮤니티 게시글 카드 (Pple Community 디자인 이식).
 * 구성: 태그·시간·조회 → 제목/발췌(+썸네일) → 구분선 → 작성자·헌혈횟수·좋아요·댓글
 */
type Props = {
  community: any;
};

/** 백엔드 Board.Category → 디자인 탭 라벨 */
export const CATEGORY_LABEL: Record<string, string> = {
  BLOOD_DONATION_REVIEW: '헌혈 후기',
  BLOOD_HOME_REVIEW: '헌혈의 집',
  QNA: '질문',
  FREE: '자유',
};

const BoardCard = ({ community }: Props) => {
  const router = useRouter();
  const writerName = community?.writer?.displayName ?? '';
  const tint = avatarTint(community?.writer?.uuid || community?.uuid || 'b');
  const thumbnail = community?.images?.[0];
  const donationCount = community?.writer?.bloodDonationCount;
  const tag = CATEGORY_LABEL[community?.category] ?? '자유';

  return (
    <Card
      onClick={() =>
        router.push(`/community/board/detail/${community.uuid}`)
      }
    >
      <TopRow>
        <Tag>{tag}</Tag>
        <Time>{formatRelativeTime(community?.createdAt)}</Time>
        <Views>조회 {community?.hits ?? 0}</Views>
      </TopRow>

      <Middle>
        <Texts>
          <Title>{community?.title}</Title>
          <Excerpt>{community?.content}</Excerpt>
        </Texts>
        {thumbnail && (
          <Thumb>
            <Image
              src={thumbnail}
              width={108}
              height={108}
              loader={() => thumbnail}
              alt=""
              style={{ borderRadius: 14, objectFit: 'cover' }}
            />
          </Thumb>
        )}
      </Middle>

      <BottomRow>
        <Avatar tint={tint.tint} ink={tint.ink}>
          {avatarInitial(writerName)}
        </Avatar>
        <Author>{writerName}</Author>
        {donationCount != null && donationCount > 0 && (
          <Badge>헌혈 {donationCount}회</Badge>
        )}
        <Metrics>
          <span>♡ {community?.likeCount ?? community?.likesCount ?? 0}</span>
          <span>💬 {community?.repliesCount ?? 0}</span>
        </Metrics>
      </BottomRow>
    </Card>
  );
};

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #ededed;
  border-radius: 20px;
  padding: 24px 26px;
  /* 카드 간 간격은 목록(Main)의 gap:16이 담당한다 — margin을 같이 주면 32px로 벌어진다. */
  display: flex;
  flex-direction: column;
  gap: 14px;
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover {
    border-color: #ffd3d3;
  }
  @media ${devices.tablet} {
    padding: 18px 16px;
    border-radius: 16px;
  }
`;
const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const Tag = styled.span`
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #fff1ed;
  color: #ff4651;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
`;
const Time = styled.span`
  font-size: 12.5px;
  color: #b7b7b7;
`;
const Views = styled.span`
  margin-left: auto;
  font-size: 12.5px;
  color: #b7b7b7;
`;
const Middle = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
`;
const Texts = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;
const Title = styled.h3`
  margin: 0;
  font-family: 'Var', sans-serif;
  font-size: 19px;
  font-weight: 700;
  line-height: 28px;
  color: #222222;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Excerpt = styled.p`
  margin: 0;
  font-size: 14.5px;
  line-height: 24px;
  color: #767676;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
`;
const Thumb = styled.div`
  width: 108px;
  height: 108px;
  flex: none;
  border-radius: 14px;
  overflow: hidden;
  background: #f4f4f4;
  @media ${devices.tablet} {
    width: 84px;
    height: 84px;
  }
`;
const BottomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 14px;
  border-top: 1px solid #f5f5f5;
`;
const Avatar = styled.div<{ tint: string; ink: string }>`
  width: 30px;
  height: 30px;
  flex: none;
  border-radius: 15px;
  background: ${({ tint }) => tint};
  color: ${({ ink }) => ink};
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Author = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: #222222;
`;
const Badge = styled.span`
  font-size: 12.5px;
  color: #b7b7b7;
`;
const Metrics = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 13px;
  color: #9a9a9a;
`;

export default BoardCard;
